/**
 * Query scope and chip definitions for the post-consultation queue.
 *
 * Separate from `PostConsultationTab` so the tab file exports only its component
 * — both for fast refresh, and because these are the parts worth testing without
 * mounting a table.
 */

import dayjs from 'dayjs';
import {
  appointmentTypeTokens,
  BariatricProcedureStage,
  BARIATRIC_PROCEDURE_STAGE_TAG_SYSTEM,
  CONSULTATION_APPOINTMENT_CODES,
} from '@bariendo/lib/constants';
import { Meta } from '@medplum/fhirtypes';
import { ListAppointment } from '@bariendo/lib/pac-list-appointments';
import { isClearedToSchedule, isNoContactSince } from '@bariendo/lib/pac-post-consultation-list';
import { ChipFilterDefinition } from '@/components/FilterChips/useChipFilters';
import { ListRange } from '@/components/Hub/listRange';
import {
  appointmentQueueSubscriptionParams,
  buildAppointmentQueueFilter,
  hasLatestConsultTag,
  isInAppointmentQueueScope,
  LATEST_CONSULT_TAG_CLAUSE,
} from './appointmentQueueScope';
import { PostConsultationLeadRow } from './postConsultationRows';

/**
 * Every consult code. Indexing `[0]` here silently scoped the whole tab to the
 * NP consult the moment a second code existed — the PN consults would simply
 * not be in the queue, with nothing to show that they were missing.
 */
const CONSULT_TYPE_TOKENS = appointmentTypeTokens(CONSULTATION_APPOINTMENT_CODES);

/**
 * Stages past Post-consultation (BAR-2986). Excluded rather than requiring
 * `post-consultation`, so a consult that ended before the hourly restage still shows.
 * Financing has its own tab.
 */
const STAGES_PAST_POST_CONSULTATION: readonly string[] = [
  BariatricProcedureStage.FINANCING_APPROVAL,
  BariatricProcedureStage.PRE_PROCEDURE,
  BariatricProcedureStage.POST_PROCEDURE,
  BariatricProcedureStage.SUBSCRIPTION_ACTIVE,
];

/** A consult as the query returns it: its participants carry the patient's tags. */
type QueueConsult = ListAppointment & {
  meta?: Meta;
  participant?: { actor?: { resource?: { resourceType?: string; meta?: Meta } } }[];
};

function isPastPostConsultation(patient: { meta?: Meta } | undefined): boolean {
  return Boolean(
    patient?.meta?.tag?.some(
      (tag) =>
        tag.system === BARIATRIC_PROCEDURE_STAGE_TAG_SYSTEM && STAGES_PAST_POST_CONSULTATION.includes(tag.code ?? '')
    )
  );
}

/**
 * The consult scope, server-side.
 *
 * `noshow` is NOT excluded: a no-showed consult belongs to this tab, which
 * ranges over consult date, and is the subject of its own chip. See
 * `isPostConsultationLead`.
 */
export function buildConsultFilter(range: ListRange): string {
  return buildAppointmentQueueFilter(CONSULT_TYPE_TOKENS, range);
}

/**
 * The hub's paged scope (BAR-2865): each patient's `latest-consult` only, already
 * started and not missed; missed consults belong to the Consultation no-show tab
 * (BAR-2809), and patients past Post-consultation (BAR-2986). Bounded at tomorrow's
 * midnight so the filter changes once a day; a consult later today is dropped by
 * the row builder.
 */
export function buildQueueConsultFilter(range: ListRange, today: Date): string {
  const tomorrow = dayjs(today).add(1, 'day').toISOString();
  return [
    `(${buildConsultFilter(range)}`,
    LATEST_CONSULT_TAG_CLAUSE,
    'status ne "noshow"',
    ...STAGES_PAST_POST_CONSULTATION.map(
      (stage) => `patient._tag ne "${BARIATRIC_PROCEDURE_STAGE_TAG_SYSTEM}|${stage}"`
    ),
    `date lt "${tomorrow}")`,
  ].join(' and ');
}

/** `isInConsultScope` plus the tag and the stage, for what live updates add to a page. */
export function isInQueueConsultScope(consult: QueueConsult, range: ListRange): boolean {
  const patient = consult.participant
    ?.map((participant) => participant.actor?.resource)
    .find((resource) => resource?.resourceType === 'Patient');
  return hasLatestConsultTag(consult) && !isPastPostConsultation(patient) && isInConsultScope(consult, range);
}

/** What the tab subscribes to — see `appointmentQueueScope` for why it is coarser than the query. */
export function postConsultationSubscriptionParams(): Record<string, string> {
  return appointmentQueueSubscriptionParams(CONSULT_TYPE_TOKENS);
}

/** The query's status and range rules, for what arrives by subscription. */
export function isInConsultScope(consult: ListAppointment, range: ListRange): boolean {
  return isInAppointmentQueueScope(consult, range);
}

/**
 * All five chips from the design, in its order.
 *
 * *Consult no-show* sits second because it is the only one that surfaces a
 * failure of the funnel rather than a state within it. It is mutually exclusive
 * with *Cleared to schedule* — a no-show has no `next-step` answer at all.
 *
 * *Overdue tasks* reads clinical Tasks. Until BAR-2585 CI's access policy
 * granted only `group-identifier=email-inbox` Tasks, so the chip was dark for
 * that role; the policy now grants `orbit-provider-task` and the chip works for
 * CI like every other role that sees this tab. (Historical note — the grant
 * widens — the same posture as *Inactive hidden* waiting on the Flag grant.
 *
 * A factory rather than a constant because two predicates need a clock, and
 * reading `new Date()` inside a predicate would make the filter
 * non-deterministic across a single render pass.
 */
export function postConsultationChips(now: Date): ChipFilterDefinition<PostConsultationLeadRow>[] {
  return [
    {
      id: 'inactive-hidden',
      label: 'Inactive hidden',
      defaultOn: true,
      removable: true,
      predicate: (row) => row.activeLead.status === 'active',
    },
    {
      id: 'consult-no-show',
      label: 'Consult no-show',
      predicate: (row) => row.consult.status === 'noshow',
    },
    {
      id: 'cleared-to-schedule',
      label: 'Cleared to schedule',
      predicate: (row) => isClearedToSchedule(row.nextSteps),
    },
    {
      id: 'no-contact-7-days',
      label: 'No contact 7 days',
      predicate: (row) => isNoContactSince(row.lastCommunication?.sent, now),
    },
    {
      id: 'overdue-tasks',
      label: 'Overdue tasks',
      predicate: (row) => row.hasOverdueTask,
    },
  ];
}
